import { CardImageSkeleton } from './lib/layout/skeleton/content/CardImageSkeleton';
import { FormSkeleton } from './lib/layout/skeleton/content/FormSkeleton';
import { FunctionCardSkeleton } from './lib/layout/skeleton/content/FunctionCardSkeleton';
import { HeadingSkeleton } from './lib/layout/skeleton/content/HeadingSkeleton';
import { HighlightsSkeleton } from './lib/layout/skeleton/content/HighlightsSkeleton';
import { InlineSigningSkeleton } from './lib/layout/skeleton/content/InlineSigningSkeleton';
import { ListItemSkeleton, ListSkeleton } from './lib/layout/skeleton/content/ListSkeleton';
import { PersonalizationCardSkeleton } from './lib/layout/skeleton/content/PersonalizationCardSkeleton';
import { ProductInfoSkeleton } from './lib/layout/skeleton/content/ProductInfoSkeleton';
import { SelectAccountSkeleton } from './lib/layout/skeleton/content/SelectAccountSkeleton';
import { TableSkeleton } from './lib/layout/skeleton/content/TableSkeleton';
import { AddressSkeleton } from './lib/layout/skeleton/content/AddressSkeleton';
import { AddressItemSkeleton } from './lib/layout/skeleton/content/AddressItemSkeleton';
import { TimelineSkeleton } from './lib/layout/skeleton/content/TimeLineSkeleton';
import { TransactionListSkeleton } from './lib/layout/skeleton/content/TransactionListSkeleton';
import { SelectableDeliveryItemsSkeleton } from './lib/layout/skeleton/content/SelectableDeliveryItemsSkeleton';

export { useProduct } from './lib/hooks/useProduct';
export { useProducts } from './lib/hooks/useProducts';
export { useCurrentAccounts } from './lib/hooks/useCurrentAccounts';
export { useCoreAccounts } from './lib/hooks/useCoreAccounts';
export { useProductData } from './lib/hooks/useProductData';
export { useCoreAccount } from './lib/hooks/useCoreAccount';
export { useLoanAccount } from './lib/hooks/useLoanAccount';
export { useLoanAccounts } from './lib/hooks/useLoanAccounts';

export {
    ActivePAPIProductProvider,
    useActivePAPIProduct,
    useUnsafeActivePAPIProduct,
} from './lib/ActivePAPIProductContext';
export {
    ActiveProductServicesProvider,
    useActiveProductServices,
} from './lib/ActiveProductServicesContext';
export { getSelectedProductIdFromURL } from './lib/services/selectedProductService';
export {
    loadAccountImage,
    loadExternalAccountImage,
    removeImageFromCache,
    saveImageToCache,
} from './lib/services/imageLoadingService';
export { preloadExternalProductImages } from './lib/services/preloadExternalProductImages';
export { isProductPapiCustomizationEnabled } from './lib/services/isProductPapiCustomizationEnabled';
export { isAliasChangeAllowed } from './lib/services/aliasChangeService';
export { fetchGAPIAccounts } from './lib/gAPIAccountEntitySliceFacade';
export {
    formatAccountNumber,
    formatAccountNo,
    formatAccountIdentifier,
} from './lib/services/accountNoService';
export {
    getBaseType,
    productsTypeService,
    registerProduct,
    getProductMenuItems,
} from './lib/services/ProductsTypeService';
export { ProductContext } from './lib/ProductContext';
export { ProductAvatar } from './lib/components/ProductAvatar';
export { ProductBadges } from './lib/components/ProductBadges';
export { LayoutStructuredInfo } from './lib/components/LayoutStructuredInfo';
export { ProductFocusPage } from './lib/ProductFocusPage';
export type { ProductService } from './lib/product-services/types';
export { ProductServices } from './lib/product-services/ProductServices';
export { ProductServicesCard } from './lib/product-services/ProductServicesCard';
export type { StripeProps } from './lib/layout/LayoutProps';
export { ActiveProductLayout } from './lib/layout/ActiveProductLayout';
export { ProductLayout } from './lib/layout/ProductLayout';
export { ProductsStripeHeader } from './lib/layout/stripe/ProductsStripeHeader';
export { ProductLayoutSkeleton } from './lib/layout/skeleton/ProductLayoutSkeleton';
export { ProductFocusPageSkeleton } from './lib/layout/skeleton/ProductFocusPageSkeleton';
export { DetailPageSkeleton } from './lib/layout/skeleton/content/DetailPageSkeleton';

export const ProductsContentSkeletons = {
    AddressSkeleton,
    AddressItemSkeleton,
    CardImageSkeleton,
    FormSkeleton,
    FunctionCardSkeleton,
    HeadingSkeleton,
    HighlightsSkeleton,
    InlineSigningSkeleton,
    ListSkeleton,
    ListItemSkeleton,
    PersonalizationCardSkeleton,
    ProductInfoSkeleton,
    SelectAccountSkeleton,
    SelectableDeliveryItemsSkeleton,
    TableSkeleton,
    TimelineSkeleton,
    TransactionListSkeleton,
};

export { MainLayout } from './lib/layout/MainLayout';
export { LayoutWithSideMenu } from './lib/layout/LayoutWithSideMenu';
export { PersonalizationCard } from './lib/personalization/PersonalizationCard';
export {
    updateGAPIAccountById,
    selectGAPIAccounts,
    gAPIAccountEntitySlice,
    useGAPIAccount,
    fetchGAPIAccount,
    deleteGAPIAccount,
    selectGAPIAccount,
    selectGAPIAccountBalance,
    selectGAPIAccountProductType,
} from './lib/gAPIAccountEntitySlice';
export {
    isInsurance,
    isCard,
    isBankCard,
    isCreditCard,
    isCreditCardAccount,
    isMainCreditCard,
    isVirtualAuthorizedCreditCard,
    isExtBank,
    isExtBankCurrentAccount,
    isDigiVVManagementAccount,
    isDigiVVSettlementAccount,
    isLoanAccount,
    isSavingsAccount,
    isSecurityAccount,
    isLegacyHRKAccount,
    isMoneyOnClickAccount,
    isLeasing,
    isSFondsPlanAccount,
    isBuildingSociety,
    isPension,
    isTermDeposit,
} from './lib/services/typeService';
export {
    getProductNumber,
    getBuildingSocietyProductNumber,
} from './lib/services/productNumberService';
export type {
    BuildingSociety,
    BuildingSocietyLoan,
    BuildSocietyLoanData,
    BuildingSocietySaving,
    BuildSocietySavingData,
    BuildingDebitInterestRateType,
    BuildingSavingInterval,
} from './lib/types/buildingSociety';
export { BuildingSavingType } from './lib/types/buildingSociety';

export { processExternalAccounts } from './lib/gAPIAccountService';
export { refreshAccountBalance } from './lib/accountBalanceService/accountBalanceService';
export { externalProductBalanceRefreshRegistry } from './lib/accountBalanceService/externalProductBalanceService';

export { PersonalizationCard as ExternalPartialPAPIPersonalizationCard } from './lib/external-partial-papi-migration/personalization-card/PersonalizationCard';
export { ColorChange as ExternalPartialPAPIColorChange } from './lib/external-partial-papi-migration/personalization-card/customizations/ColorChange';
export { AliasChange as ExternalPartialPAPIAliasChange } from './lib/external-partial-papi-migration/personalization-card/customizations/AliasChange';
export { ProductImageUpload as ExternalPartialPAPIImageChange } from './lib/external-partial-papi-migration/personalization-card/components/ProductImageUpload';
export { useDashboardAppearanceChange as useExternalPartialPAPIDashboardAppearanceChange } from './lib/external-partial-papi-migration/personalization-card/customizations/hooks/useDashboardAppearanceChange';
export { PersonalizationCard as PartialPAPIPersonalizationCard } from './lib/partial-papi-migration/personalization-card/PersonalizationCard';
export { ColorChange as PartialPAPIColorChange } from './lib/partial-papi-migration/personalization-card/customizations/ColorChange';
export { AliasChange as PartialPAPIAliasChange } from './lib/partial-papi-migration/personalization-card/customizations/AliasChange';
export { ProductImageUpload as PartialPAPIImageChange } from './lib/partial-papi-migration/ProductImageUpload';
export { ProductImageUpload } from './lib/personalization/ProductImageUpload';

export type { ActivePAPIProductWithAvatarData } from './lib/external-partial-papi-migration/personalization-card/types';
export { PersonalizationCard as PAPIPersonalizationCard } from './lib/papi-migration/personalization-card/PersonalizationCard';
export { ColorChange as PAPIColorChange } from './lib/papi-migration/personalization-card/customizations/ColorChange';
export { AliasChange as PAPIAliasChange } from './lib/papi-migration/personalization-card/customizations/AliasChange';
export { DashboardAppearanceChange as PAPIDashboardAppearanceChange } from './lib/papi-migration/personalization-card/customizations/DashboardAppearanceChange';
export { unsupportedPAPIRequest } from './lib/papi-migration/requestService';
export { getProductImage } from './lib/papi-migration/hooks/useProductImage';
export { isFlagAndFeatureDrivenSelfServicing } from './lib/services/selfServicingService';

export { getActionsDropdownComponent } from './lib/overview-actions-dropdown/ActionsDropdownService';
export { changeProductVisibility } from './lib/overview-actions-dropdown/changeProductVisibility';
export { CardMenuDropdownToggle } from './lib/overview-actions-dropdown/CardMenuDropdownToggle';
